// Asking the league a poll (PLAN-2026-10-30-polls-in-chat §4): a question, two
// to six options and how long it runs, in the chat's tray above the box where
// the emoji and the stickers open. It says what is wrong in words before it
// sends, from the same rules the server keeps (`CreatePollRequest`).

import { useState, type KeyboardEvent } from 'react';
import {
  CreatePollRequest,
  POLL_OPTION_MAX,
  POLL_OPTIONS_MAX,
  POLL_OPTIONS_MIN,
  POLL_QUESTION_MAX,
  type CreatePollInput,
  type PollRuns,
} from '@fsp/domain';
import { Button, IconButton, Segments, TextField } from '../kit/ui';

const RUNS: readonly { id: PollRuns; label: string }[] = [
  { id: 'day', label: 'A day' },
  { id: 'three-days', label: 'Three days' },
  { id: 'week', label: 'A week' },
];

export function PollComposer({
  onAsk,
  onClose,
}: {
  /** Resolves to null once it is up, or to the sentence to show. */
  onAsk: (poll: CreatePollInput) => Promise<string | null>;
  onClose: () => void;
}) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [runs, setRuns] = useState<PollRuns>('day');
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const ask = async () => {
    const poll = { question, options, runs };
    const checked = CreatePollRequest.safeParse(poll);
    if (!checked.success) {
      setProblem(checked.error.issues[0]?.message ?? 'Check the poll and try again.');
      return;
    }
    setSending(true);
    const refused = await onAsk(checked.data);
    setSending(false);
    if (refused !== null) setProblem(refused);
    else onClose();
  };
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    onClose();
  };

  return (
    <div className="fsp-pollform" role="dialog" aria-label="New poll" onKeyDown={keys}>
      <div className="fsp-pollform-head">
        <span className="fsp-pollform-title">New poll</span>
        <IconButton icon="close" label="Close the poll" variant="quiet" onClick={onClose} />
      </div>
      <div className="fsp-pollform-body">
        <TextField
          label="Question"
          placeholder="Pizza or tacos for draft night?"
          value={question}
          onChange={(v) => {
            setQuestion(v);
            setProblem(null);
          }}
          maxLength={POLL_QUESTION_MAX}
          autoFocus
        />
        {options.map((o, i) => (
          <div key={i} className="fsp-pollform-option">
            <TextField
              label={`Option ${i + 1}`}
              hideLabel
              grow
              placeholder={`Option ${i + 1}`}
              value={o}
              onChange={(v) => {
                setOptions((all) => all.map((x, j) => (j === i ? v : x)));
                setProblem(null);
              }}
              maxLength={POLL_OPTION_MAX}
            />
            {options.length > POLL_OPTIONS_MIN ? (
              <IconButton
                icon="remove"
                label={`Remove option ${i + 1}`}
                variant="quiet"
                size="sm"
                onClick={() => setOptions((all) => all.filter((_, j) => j !== i))}
              />
            ) : null}
          </div>
        ))}
        {options.length < POLL_OPTIONS_MAX ? (
          <Button
            size="xs"
            variant="quiet"
            icon="add"
            onClick={() => setOptions((all) => [...all, ''])}
          >
            Add an option
          </Button>
        ) : null}
        <Segments label="Runs for" items={RUNS} value={runs} onChange={setRuns} />
        {problem !== null ? (
          <p className="fsp-pollform-problem" role="alert">
            {problem}
          </p>
        ) : null}
        <Button variant="primary" full loading={sending} onClick={() => void ask()}>
          Ask the league
        </Button>
      </div>
    </div>
  );
}
